import { normalizeTutorialState } from "./tutorial-state.mjs";

export const TUTORIAL_CONTEXTS = Object.freeze({
  overworld: "overworld",
  tactical: "tactical",
  baseDefense: "baseDefense",
});

export const TUTORIAL_LESSONS = Object.freeze([
  Object.freeze({
    id: "overworld.pick-target",
    title: "Pick a target",
    context: Object.freeze([TUTORIAL_CONTEXTS.overworld]),
    priority: 10,
    body: Object.freeze([
      Object.freeze({
        type: "paragraph",
        text: "To survive, you need to scout your area and collect supplies for your community. Click a building on the map to preview the supply run, route, and any expected obstacles.",
      }),
      Object.freeze({
        type: "callout",
        text: "Unknown buildings are still valid targets. Once you start the run, you'll uncover more information about the buildings and the potential supply amounts, if any.",
      }),
      Object.freeze({ type: "animation", variant: "target" }),
    ]),
    showWhen: ({ screen }) => screen === TUTORIAL_CONTEXTS.overworld,
  }),
  Object.freeze({
    id: "supply-run.intro",
    title: "Supply run basics",
    context: Object.freeze([TUTORIAL_CONTEXTS.tactical]),
    priority: 15,
    body: Object.freeze([
      Object.freeze({
        type: "paragraph",
        text: "On a supply run, your job is to search buildings, recover food, building materials, and equipment, avoid contact, and get everyone back to base safely.",
      }),
      Object.freeze({
        type: "paragraph",
        text: "Search buildings that look promising, then extract before the area gets too dangerous. Supplies only matter if someone carries them out.",
      }),
      Object.freeze({
        type: "callout",
        text: "Combat is a last resort. Fighting can get you out when cornered, but it raises the threat meter, makes future encounters harder, and gives no experience or rewards.",
      }),
      Object.freeze({ type: "animation", variant: "target" }),
    ]),
    showWhen: ({ screen, tacticalActive }) => screen === TUTORIAL_CONTEXTS.tactical && tacticalActive,
  }),
  Object.freeze({
    id: "tactical.move-one-turn",
    title: "Move one turn at a time",
    context: Object.freeze([TUTORIAL_CONTEXTS.tactical]),
    priority: 20,
    body: Object.freeze([
      Object.freeze({
        type: "paragraph",
        text: "Select a survivor, then move your cursor to preview the movement. Click to move the unit (for now, there is no undo button). If you end your movement near buildings, you will uncover more information about the buildings and any potential supplies. If you end your movement directly on a building, you will immediately begin scavenging.",
      }),
      Object.freeze({
        type: "callout",
        text: "When all units are done moving, press [SPACEBAR] to end your turn.",
      }),
      Object.freeze({ type: "animation", variant: "movement" }),
    ]),
    showWhen: ({ screen, tacticalActive }) => screen === TUTORIAL_CONTEXTS.tactical && tacticalActive,
  }),
  Object.freeze({
    id: "tactical.watch-cones",
    title: "Watch vision cones",
    context: Object.freeze([TUTORIAL_CONTEXTS.tactical]),
    priority: 30,
    body: Object.freeze([
      Object.freeze({
        type: "paragraph",
        text: "Hostiles will attack if they see you in their vision cones. You can slip behind or beside a patrol, but noise and lingering still raise the threat level.",
      }),
      Object.freeze({
        type: "callout",
        text: "Click on a hostile to preview their next move, if any. Some enemies patrol a route, others guard a single location and rotate from time to time.",
      }),
      Object.freeze({ type: "animation", variant: "vision" }),
    ]),
    showWhen: ({ screen, tacticalActive }) => screen === TUTORIAL_CONTEXTS.tactical && tacticalActive,
  }),
]);

export function availableTutorialLessons(context, tutorialState) {
  const state = normalizeTutorialState(tutorialState);
  const dismissed = new Set(state.dismissedLessonIds);
  return TUTORIAL_LESSONS
    .filter((lesson) => !dismissed.has(lesson.id))
    .filter((lesson) => lesson.context.includes(context?.screen))
    .filter((lesson) => !lesson.showWhen || lesson.showWhen(context ?? {}))
    .map((lesson) => ({
      ...lesson,
      complete: state.completedLessonIds.includes(lesson.id),
    }))
    .sort((a, b) => a.priority - b.priority || a.title.localeCompare(b.title));
}
